import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Download, RefreshCw, Search, Users, Target, Activity, BookOpen, Wallet, ChevronRight } from 'lucide-react';
import { getPreviewAuthHeaders } from '../lib/firebase';
import { NAVY, TEAL, PLAN_NAMES, formatOne, dayLabel, Card, Stat, Columns, HBar } from './AnalyticsParts';

// =====================================================================
// Admin Analytics, Students view (round B, 2026-10-25). A searchable table
// of every student with plan, goal exam, practice and readiness, and a
// profile page per student. Data: GET /api/admin/students and
// GET /api/admin/students/:uid (admin only).
// =====================================================================

interface StudentRow {
  uid: string; name: string; email: string; createdAt: string;
  plan: string; state: 'active' | 'expired' | 'free'; planEnds: string;
  goalPaperId: string; goalLabel: string; targetState: string; examDate: string; daysToExam: number | null;
  answered: number; accuracy: number | null; tests: number; lastTestPct: number | null; lastActive: string;
  readiness: number | null; readinessLabel: string; paid: Record<string, number>; finishedSignup: boolean;
}
interface PaperProgress {
  paperId: string; label: string; answered: number; attempts: number; accuracy: number | null; lastActive: string;
  tests: number; readiness: number | null; readinessLabel: string;
  domains: { name: string; correct: number; total: number; accuracy: number }[];
}
interface StudentProfile {
  generatedAt: string;
  profile: {
    uid: string; name: string; email: string; createdAt: string; plan: string; state: StudentRow['state']; planEnds: string;
    goalPaperId: string; goalLabel: string; goalFamily: string; targetState: string; examDate: string; daysToExam: number | null;
    dailyGoalMinutes: number | null; streakDays: number; onboardingCompletedAt: string; emailVerified: boolean;
  };
  totals: { questionsAnswered: number; attempts: number; accuracy: number | null; tests: number; flashcardsReviewed: number; activeDaysLast30: number; lastActive: string };
  goal: PaperProgress | null;
  papers: PaperProgress[];
  tests: { id: string; paperId: string; label: string; percentage: number; score: number | null; passed: boolean; minutes: number; mode: string; completedAt: string }[];
  weekly: { weekStart: string; questions: number; correct: number }[];
  payments: { amount: number; currency: string; tier: string; paidAt: string; channel: string }[];
  freeQuestions: { paperId: string; label: string; answered: number }[];
}

type Filter = 'all' | 'active' | 'expired' | 'free' | 'soon' | 'idle' | 'notstarted';
const FILTERS: { id: Filter; label: string; test: (s: StudentRow, now: number) => boolean }[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'active', label: 'Paying', test: (s) => s.state === 'active' },
  { id: 'expired', label: 'Expired', test: (s) => s.state === 'expired' },
  { id: 'free', label: 'Free', test: (s) => s.state === 'free' },
  { id: 'soon', label: 'Exam in 30 days', test: (s) => s.daysToExam !== null && s.daysToExam >= 0 && s.daysToExam <= 30 },
  { id: 'idle', label: 'Quiet for 14 days', test: (s, now) => !s.lastActive || now - Date.parse(s.lastActive) > 14 * 86400000 },
  { id: 'notstarted', label: 'Not started', test: (s) => s.answered === 0 }
];

type Sort = 'newest' | 'active' | 'readyLow' | 'readyHigh' | 'exam' | 'practice';
const SORTS: { id: Sort; label: string }[] = [
  { id: 'newest', label: 'Newest first' },
  { id: 'active', label: 'Last active' },
  { id: 'exam', label: 'Exam date, soonest' },
  { id: 'readyLow', label: 'Readiness, lowest' },
  { id: 'readyHigh', label: 'Readiness, highest' },
  { id: 'practice', label: 'Most practice' }
];

const STATE_LABEL: Record<StudentRow['state'], string> = { active: 'Paying', expired: 'Expired', free: 'Free' };
const STATE_STYLE: Record<StudentRow['state'], string> = {
  active: 'bg-teal-50 text-teal-800 border-teal-200',
  expired: 'bg-amber-50 text-amber-800 border-amber-200',
  free: 'bg-slate-50 text-slate-600 border-slate-200'
};

function ago(iso: string): string {
  const ms = Date.parse(iso);
  if (!ms) return 'Never';
  const days = Math.floor((Date.now() - ms) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months} month${months === 1 ? '' : 's'} ago` : `${Math.floor(months / 12)} year${months >= 24 ? 's' : ''} ago`;
}
function examWhen(days: number | null, date: string): string {
  if (days === null) return date ? dayLabel(date) : 'No date';
  if (days < 0) return `Was ${dayLabel(date)}`;
  if (days === 0) return 'Today';
  return `In ${days} day${days === 1 ? '' : 's'}`;
}
function planName(plan: string): string {
  return PLAN_NAMES[plan] || (plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : 'Free');
}

const StateBadge: React.FC<{ state: StudentRow['state']; plan: string }> = ({ state, plan }) => (
  <span className={`inline-block px-2 py-0.5 rounded-md border text-[11px] font-bold whitespace-nowrap ${STATE_STYLE[state]}`}>
    {state === 'free' ? 'Free' : `${STATE_LABEL[state]} · ${planName(plan)}`}
  </span>
);

const Readiness: React.FC<{ score: number | null; label: string; wide?: boolean }> = ({ score, label, wide }) => (
  <div className={wide ? 'w-full' : 'w-28'}>
    <div className="flex items-baseline justify-between gap-2 text-[12px]">
      <span className="font-semibold text-slate-700 whitespace-nowrap">{label}</span>
      {score !== null && <span className="font-bold text-slate-900 tabular-nums">{score}</span>}
    </div>
    <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${score ?? 0}%`, background: score !== null && score >= 70 ? NAVY : TEAL }} />
    </div>
  </div>
);

function toCsv(rows: StudentRow[]): string {
  const head = ['Name', 'Email', 'Joined', 'Status', 'Plan', 'Plan ends', 'Goal exam', 'State', 'Exam date', 'Questions answered', 'Accuracy %', 'Tests taken', 'Last test %', 'Readiness', 'Readiness label', 'Last active', 'Paid'];
  const esc = (v: any) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = rows.map((r) => [
    r.name, r.email, r.createdAt.slice(0, 10), STATE_LABEL[r.state], planName(r.plan), r.planEnds.slice(0, 10), r.goalLabel, r.targetState,
    r.examDate, r.answered, r.accuracy ?? '', r.tests, r.lastTestPct ?? '', r.readiness ?? '', r.readinessLabel, r.lastActive.slice(0, 10),
    Object.entries(r.paid).map(([c, v]) => `${c} ${v}`).join(' + ')
  ].map(esc).join(','));
  return [head.join(','), ...lines].join('\n');
}

// ---------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------
const StudentProfileView: React.FC<{ uid: string; fallback?: StudentRow; onBack: () => void }> = ({ uid, fallback, onBack }) => {
  const [data, setData] = useState<StudentProfile | null>(null);
  const [error, setError] = useState('');
  const [paperId, setPaperId] = useState<string>('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const headers = await getPreviewAuthHeaders();
        const res = await fetch(`/api/admin/students/${encodeURIComponent(uid)}`, { headers });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || `The server answered ${res.status}`);
        if (alive) {
          setData(body as StudentProfile);
          setPaperId((body as StudentProfile).goal?.paperId || (body as StudentProfile).papers[0]?.paperId || '');
        }
      } catch (e: any) {
        if (alive) setError(e?.message || 'Could not load this student');
      }
    })();
    return () => { alive = false; };
  }, [uid]);

  const back = (
    <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900 cursor-pointer">
      <ArrowLeft className="w-4 h-4" /> All students
    </button>
  );

  if (!data) {
    return (
      <div className="space-y-4">
        {back}
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500">
          {error ? <>Could not load {fallback?.name || fallback?.email || 'this student'}: {error}</> : 'Loading profile…'}
        </div>
      </div>
    );
  }

  const p = data.profile;
  const t = data.totals;
  const paper = data.papers.find((x) => x.paperId === paperId) || null;
  const weakest = paper ? paper.domains.filter((d) => d.total >= 3 && d.accuracy < 70).slice(0, 2) : [];

  return (
    <div className="space-y-5">
      {back}

      <section className="min-w-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-extrabold text-slate-900 truncate">{p.name || p.email || 'Student'}</h2>
            <div className="text-sm text-slate-500 truncate">{p.email}</div>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-500">
              <StateBadge state={p.state} plan={p.plan} />
              {p.planEnds && p.state !== 'free' && <span>{p.state === 'active' ? 'Renews or ends' : 'Ended'} {dayLabel(p.planEnds)}</span>}
              <span>Joined {p.createdAt ? dayLabel(p.createdAt) : 'unknown'}</span>
              <span>Last active {ago(t.lastActive)}</span>
              {!p.onboardingCompletedAt && <span className="text-amber-700 font-semibold">Has not finished sign up</span>}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 p-4 min-w-[240px]">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" style={{ color: TEAL }} /> Goal exam</div>
            <div className="text-sm font-bold text-slate-900 mt-1">{p.goalLabel || 'Not chosen'}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {[p.targetState, examWhen(p.daysToExam, p.examDate)].filter(Boolean).join(' · ')}
            </div>
            {data.goal ? (
              <div className="mt-3"><Readiness score={data.goal.readiness} label={data.goal.readinessLabel} wide /></div>
            ) : p.goalPaperId ? (
              <div className="mt-3 text-xs text-slate-500">No practice on this exam yet.</div>
            ) : null}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Questions answered" value={t.questionsAnswered.toLocaleString()} sub={`${t.attempts.toLocaleString()} attempts`} />
        <Stat label="Accuracy" value={t.accuracy === null ? 'None yet' : `${t.accuracy}%`} />
        <Stat label="Tests taken" value={String(t.tests)} />
        <Stat label="Active days" value={String(t.activeDaysLast30)} sub="In the last 30 days" />
        <Stat label="Flashcards" value={String(t.flashcardsReviewed)} sub="Cards reviewed" />
      </div>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-5">
        <Card
          title="Progress by topic"
          icon={BookOpen}
          right={data.papers.length > 1 ? (
            <select value={paperId} onChange={(e) => setPaperId(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white max-w-[200px]">
              {data.papers.map((x) => <option key={x.paperId} value={x.paperId}>{x.label}{x.paperId === p.goalPaperId ? ' (goal)' : ''}</option>)}
            </select>
          ) : undefined}
        >
          {!paper ? (
            <p className="text-sm text-slate-400">No practice recorded yet.</p>
          ) : (
            <>
              <div className="text-xs text-slate-500 mb-3">
                {paper.label}: {paper.answered} questions, {paper.accuracy ?? 0}% correct, {paper.tests} test{paper.tests === 1 ? '' : 's'}
              </div>
              <div className="space-y-3">
                {paper.domains.map((d) => (
                  <HBar key={d.name} label={d.name} value={d.accuracy} max={100} right={`${d.accuracy}% of ${d.total}`} color={d.accuracy >= 70 ? NAVY : TEAL} />
                ))}
              </div>
              {weakest.length > 0 && (
                <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
                  Weakest so far: <span className="font-semibold text-slate-800">{weakest.map((w) => `${w.name} (${w.accuracy}%)`).join(' and ')}</span>
                </div>
              )}
            </>
          )}
        </Card>

        <Card title="Questions per week" icon={Activity}>
          <Columns
            height={140}
            items={data.weekly.map((w) => ({
              label: dayLabel(w.weekStart),
              value: w.questions,
              tip: `Week of ${dayLabel(w.weekStart)}: ${w.questions} questions, ${w.questions ? Math.round((w.correct / w.questions) * 100) : 0}% correct`
            }))}
          />
          {data.papers.length > 0 && (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-1.5 pr-2 font-semibold">Paper</th>
                    <th className="py-1.5 pr-2 font-semibold text-right">Questions</th>
                    <th className="py-1.5 pr-2 font-semibold text-right">Correct</th>
                    <th className="py-1.5 font-semibold text-right">Readiness</th>
                  </tr>
                </thead>
                <tbody>
                  {data.papers.map((x) => (
                    <tr key={x.paperId} className="border-b border-slate-100 last:border-0">
                      <td className="py-1.5 pr-2 text-slate-800 truncate max-w-[160px]">{x.label}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{x.answered}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{x.accuracy ?? 0}%</td>
                      <td className="py-1.5 text-right tabular-nums">{x.readiness ?? x.readinessLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card title="Tests and mock exams" icon={BookOpen}>
        {data.tests.length === 0 ? (
          <p className="text-sm text-slate-400">No tests taken yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3 font-semibold">Date</th>
                  <th className="py-2 pr-3 font-semibold">Paper</th>
                  <th className="py-2 pr-3 font-semibold text-right">Score</th>
                  <th className="py-2 pr-3 font-semibold text-right">Correct</th>
                  <th className="py-2 pr-3 font-semibold">Result</th>
                  <th className="py-2 font-semibold text-right">Time</th>
                </tr>
              </thead>
              <tbody>
                {data.tests.map((x) => (
                  <tr key={x.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{x.completedAt ? dayLabel(x.completedAt) : ''}</td>
                    <td className="py-2 pr-3 text-slate-800 truncate max-w-[260px]">{x.label}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{x.score ?? ''}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-bold text-slate-900">{x.percentage}%</td>
                    <td className="py-2 pr-3 text-slate-700">{x.passed ? 'Passed' : 'Below passing'}</td>
                    <td className="py-2 text-right tabular-nums text-slate-500">{x.minutes} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid md:grid-cols-2 gap-5">
        <Card title="Payments" icon={Wallet}>
          {data.payments.length === 0 ? (
            <p className="text-sm text-slate-400">No payments recorded.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-[13px]">
              {data.payments.map((x, i) => (
                <li key={i} className="py-2 flex justify-between gap-3">
                  <span className="text-slate-600">{x.paidAt ? dayLabel(x.paidAt) : ''} · {planName(x.tier)}</span>
                  <span className="font-bold text-slate-900 tabular-nums">{formatOne(x.amount, x.currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Free questions used" icon={Target}>
          {data.freeQuestions.length === 0 ? (
            <p className="text-sm text-slate-400">None used on this account.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-[13px]">
              {data.freeQuestions.map((x) => (
                <li key={x.paperId} className="py-2 flex justify-between gap-3">
                  <span className="text-slate-700 truncate">{x.label}</span>
                  <span className="font-bold text-slate-900 tabular-nums">{x.answered}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------
export const AdminStudents: React.FC = () => {
  const [rows, setRows] = useState<StudentRow[] | null>(null);
  const [generatedAt, setGeneratedAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('newest');
  const [shown, setShown] = useState(50);
  const [openUid, setOpenUid] = useState<string | null>(null);

  const load = async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const headers = await getPreviewAuthHeaders();
      const res = await fetch(`/api/admin/students${refresh ? '?refresh=1' : ''}`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `The server answered ${res.status}`);
      setRows(body.students || []);
      setGeneratedAt(body.generatedAt || '');
    } catch (e: any) {
      setError(e?.message || 'Could not load students');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(false); }, []);

  const now = Date.now();
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    FILTERS.forEach((f) => { out[f.id] = (rows || []).filter((r) => f.test(r, now)).length; });
    return out;
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter)!;
    const q = search.trim().toLowerCase();
    const list = (rows || []).filter((r) => f.test(r, now) && (!q || `${r.name} ${r.email} ${r.goalLabel} ${r.targetState}`.toLowerCase().includes(q)));
    const time = (s: string) => Date.parse(s) || 0;
    const ready = (r: StudentRow, empty: number) => (r.readiness === null ? empty : r.readiness);
    list.sort((a, b) => {
      switch (sort) {
        case 'active': return time(b.lastActive) - time(a.lastActive);
        case 'exam': {
          const da = a.daysToExam !== null && a.daysToExam >= 0 ? a.daysToExam : 1e9;
          const db = b.daysToExam !== null && b.daysToExam >= 0 ? b.daysToExam : 1e9;
          return da - db;
        }
        case 'readyLow': return ready(a, 1e9) - ready(b, 1e9);
        case 'readyHigh': return ready(b, -1) - ready(a, -1);
        case 'practice': return b.answered - a.answered;
        default: return time(b.createdAt) - time(a.createdAt);
      }
    });
    return list;
  }, [rows, filter, search, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setShown(50); }, [filter, search, sort]);

  const exportCsv = () => {
    const blob = new Blob([toCsv(visible)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prepraxis-students-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (openUid) {
    return <StudentProfileView uid={openUid} fallback={(rows || []).find((r) => r.uid === openUid)} onBack={() => setOpenUid(null)} />;
  }

  if (!rows) {
    return (
      <div className="py-16 text-center">
        {loading ? <p className="text-sm text-slate-500">Loading students…</p> : (
          <>
            <p className="text-slate-700 font-semibold">Students could not load.</p>
            <p className="text-sm text-slate-500 mt-1">{error}</p>
            <button onClick={() => load(true)} className="mt-4 px-4 py-2 rounded-lg text-white text-sm font-bold cursor-pointer" style={{ background: TEAL }}>Try again</button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          {rows.length.toLocaleString()} students{generatedAt ? ` · Updated ${new Date(generatedAt).toLocaleString()}` : ''}
        </p>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer">
            <Download className="w-3.5 h-3.5" /> Download list
          </button>
          <button onClick={() => load(true)} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>
      {error && <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-2.5">{error}</div>}

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-100 space-y-3">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, exam or state"
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#0E9594]"
              />
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white">
              {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border cursor-pointer transition ${filter === f.id ? 'text-white border-transparent' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                style={filter === f.id ? { background: NAVY } : undefined}
              >
                {f.label} <span className="tabular-nums opacity-80">{counts[f.id] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No students match.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2.5 px-4 font-semibold">Student</th>
                    <th className="py-2.5 pr-3 font-semibold">Plan</th>
                    <th className="py-2.5 pr-3 font-semibold">Goal exam</th>
                    <th className="py-2.5 pr-3 font-semibold text-right">Practice</th>
                    <th className="py-2.5 pr-3 font-semibold">Readiness</th>
                    <th className="py-2.5 pr-4 font-semibold">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.slice(0, shown).map((r) => (
                    <tr key={r.uid} onClick={() => setOpenUid(r.uid)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                      <td className="py-2.5 px-4 max-w-[240px]">
                        <div className="font-semibold text-slate-900 truncate">{r.name || r.email || 'No name'}</div>
                        <div className="text-[11.5px] text-slate-500 truncate">{r.email}</div>
                      </td>
                      <td className="py-2.5 pr-3"><StateBadge state={r.state} plan={r.plan} /></td>
                      <td className="py-2.5 pr-3 max-w-[220px]">
                        <div className="text-slate-800 truncate">{r.goalLabel || <span className="text-slate-400">Not chosen</span>}</div>
                        {(r.examDate || r.targetState) && (
                          <div className="text-[11.5px] text-slate-500 truncate">{[r.targetState, r.examDate ? examWhen(r.daysToExam, r.examDate) : ''].filter(Boolean).join(' · ')}</div>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right whitespace-nowrap">
                        <div className="font-bold text-slate-900 tabular-nums">{r.answered}</div>
                        <div className="text-[11.5px] text-slate-500">{r.accuracy === null ? 'No answers' : `${r.accuracy}% correct`}</div>
                      </td>
                      <td className="py-2.5 pr-3"><Readiness score={r.readiness} label={r.readinessLabel} /></td>
                      <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">{ago(r.lastActive)} <ChevronRight className="w-3.5 h-3.5 text-slate-300" /></span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Phone list */}
            <ul className="md:hidden divide-y divide-slate-100">
              {visible.slice(0, shown).map((r) => (
                <li key={r.uid}>
                  <button onClick={() => setOpenUid(r.uid)} className="w-full text-left p-4 hover:bg-slate-50 cursor-pointer space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{r.name || r.email || 'No name'}</div>
                        <div className="text-xs text-slate-500 truncate">{r.goalLabel || 'No goal exam'}</div>
                      </div>
                      <StateBadge state={r.state} plan={r.plan} />
                    </div>
                    <div className="flex items-end justify-between gap-3">
                      <Readiness score={r.readiness} label={r.readinessLabel} />
                      <div className="text-[11.5px] text-slate-500 text-right">{r.answered} questions<br />{ago(r.lastActive)}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            {visible.length > shown && (
              <div className="p-3 text-center border-t border-slate-100">
                <button onClick={() => setShown((n) => n + 50)} className="text-sm font-bold cursor-pointer" style={{ color: TEAL }}>
                  Show more ({visible.length - shown} left)
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <p className="text-[11px] text-slate-400 flex items-start gap-1.5">
        <Users className="w-3.5 h-3.5 shrink-0 mt-px" />
        Readiness is for the goal exam. It blends how often answers are right, recent test scores and how much of the question bank has been covered. Under 10 questions on the goal exam reads as Not started.
      </p>
    </div>
  );
};

export default AdminStudents;
